import { t } from "i18next";
import { OrderRequestData } from "../Domain/RemoteOrderRequests.types";
import { Theme } from "@mui/material";
import { DateTime } from "luxon";
import { ServiceDeliveryStatus } from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";

/** Column order: Received → In Progress → Ready → Archive → Cancelled */
export const ORDER_REQUEST_LIST_COLUMN_COUNT = 5;

/** Stable ids for @hello-pangea/dnd `droppableId` (index in array matches column index 0–4). */
export const ORDER_REQUEST_DROPPABLE_IDS = [
  "order-request-column-received",
  "order-request-column-in-progress",
  "order-request-column-ready",
  "order-request-column-archive",
  "order-request-column-cancelled"
] as const;

export type OrderRequestDroppableId = (typeof ORDER_REQUEST_DROPPABLE_IDS)[number];

/** Resolves a kanban column index from `droppableId`, or `-1` if unknown. */
export function getOrderRequestColumnIndex(droppableId: string): number {
  return ORDER_REQUEST_DROPPABLE_IDS.indexOf(droppableId as OrderRequestDroppableId);
}

export const ORDER_REQUEST_COLUMN_ARCHIVE = 3;
export const ORDER_REQUEST_COLUMN_CANCELLED = 4;

/** Archive and Cancelled are read-only for drag-and-drop (nothing from/to Cancelled). */
export const isLockedOrderRequestColumn = (itemIndex: number) =>
  itemIndex === ORDER_REQUEST_COLUMN_ARCHIVE || itemIndex === ORDER_REQUEST_COLUMN_CANCELLED;

export const listTitle = (itemIndex: number, listItems: OrderRequestData[][]) => {
  switch (itemIndex) {
    case 0:
      return t("OrderList_Title_Received", { number: listItems[itemIndex].length });
    case 1:
      return t("OrderList_Title_InProgress", { number: listItems[itemIndex].length });
    case 2:
      return t("OrderList_Title_Ready", { number: listItems[itemIndex].length });
    case 3:
      return t("OrderList_Title_Archive", { number: listItems[itemIndex].length });
    case 4:
      return t("OrderList_Title_Cancelled", { number: listItems[itemIndex].length });
    default:
      return `${t("List")} ${itemIndex}`;
  }
};

export const listBgColor = (isDraggingOver: boolean, theme: Theme) => {
  const draggingBg = theme.palette.mode === "dark" ? "#34353e" : "#d2d2d2";
  return isDraggingOver ? draggingBg : theme.palette.background.paper;
};

export const filterDataByStatus = (data: OrderRequestData[]) => {
  const cancelledItems = data.filter(item => item.status === BookingRequestsStatus.CANCELLED);

  const archiveItems = data.filter(
    item =>
      item.status !== BookingRequestsStatus.CANCELLED &&
      DateTime.fromISO(item.date).diffNow().valueOf() <= 0
  );

  const wipItems = data.filter(
    item =>
      item.status !== BookingRequestsStatus.CANCELLED &&
      DateTime.fromISO(item.date).diffNow().valueOf() > 0
  );
  const receivedItems = wipItems.filter(
    item => item.deliveryStatus === ServiceDeliveryStatus.ORDERED
  );
  const progressItems = wipItems.filter(
    item => item.deliveryStatus === ServiceDeliveryStatus.IN_PROGRESS
  );
  const preparedItems = wipItems.filter(
    item => item.deliveryStatus === ServiceDeliveryStatus.READY
  );

  return {
    archiveItems,
    cancelledItems,
    receivedItems,
    progressItems,
    preparedItems
  };
};

export const findTargetStatus = (targetListIndex: number) => {
  switch (targetListIndex) {
    case 0:
      return ServiceDeliveryStatus.ORDERED;
    case 1:
      return ServiceDeliveryStatus.IN_PROGRESS;
    case 2:
      return ServiceDeliveryStatus.READY;
    default:
      return ServiceDeliveryStatus.ORDERED;
  }
};
